import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import React, { useState, useRef, DragEvent, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Document, Page, pdfjs } from "react-pdf";
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { PDFDocument } from "pdf-lib";
import Button from "../common/Button";
import Input from "../common/Input";
import TextArea from "../Settings/TextArea";
import { startRedactionUpload } from "../../services/redactionUploadService";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface RedactionUploadProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  // A PDF dropped on the page behind the modal; the page opens the dialog and
  // hands it over here. A new File object is one drop.
  droppedFile?: File | null;
}

const MAX_PDF_SIZE_MB = 50;

const RedactionUpload: React.FC<RedactionUploadProps> = ({
  modalRef,
  droppedFile,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [documentName, setDocumentName] = useState("");
  const [redactionInstructions, setRedactionInstructions] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile]
  );

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const resetModal = () => {
    setSelectedFile(null);
    setIsDragOver(false);
    setPreviewPage(1);
    setPageCount(0);
    setDocumentName("");
    setRedactionInstructions("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Clear staged state whenever the dialog is dismissed, so reopening the
  // modal never shows the previous file (mirrors UploadModal).
  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;
    const observer = new MutationObserver(() => {
      if (!dialog.hasAttribute("open")) {
        resetModal();
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [modalRef]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = async (file: File) => {
    if (file.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }
    if (file.size > MAX_PDF_SIZE_MB * 1024 * 1024) {
      toast.error(`File size must be less than ${MAX_PDF_SIZE_MB}MB`);
      return;
    }

    setSelectedFile(file);
    setPreviewPage(1);

    if (!documentName) {
      setDocumentName(file.name.replace(/\.pdf$/i, ""));
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer, {
        ignoreEncryption: true,
      });
      setPageCount(pdfDoc.getPageCount());
    } catch (error) {
      console.error("Error reading PDF:", error);
      setPageCount(0);
    }
  };

  // A PDF dropped on the page behind the modal goes through the same checks as
  // one chosen in the dialog.
  useEffect(() => {
    if (droppedFile) void handleFileSelect(droppedFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFile]);

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPageCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Hand the file to the upload store and close: the tray shows progress and
  // the redaction status, and keeps going if the officer navigates away.
  const handleUpload = () => {
    if (!selectedFile) {
      toast.error("Please select a PDF file");
      return;
    }
    if (!documentName.trim()) {
      toast.error("Please enter a document name");
      return;
    }

    startRedactionUpload({
      file: selectedFile,
      name: documentName.trim(),
      pageCount,
      instructions: redactionInstructions,
    });
    modalRef.current?.close();
    resetModal();
  };

  const handleClose = () => {
    if (
      selectedFile &&
      !window.confirm(
        "You have an unsaved document. Are you sure you want to leave?"
      )
    ) {
      return;
    }
    modalRef.current?.close();
  };

  // Block navigation while a file is staged (mirrors the form upload).
  useEffect(() => {
    if (!selectedFile) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [selectedFile]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Upload Document to Redact</h3>
          <button onClick={handleClose}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* File Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors hover:bg-gray-100 dark:hover:bg-base-200 cursor-pointer ${
            isDragOver
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
              : "border-gray-300 dark:border-base-300"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            accept="application/pdf"
            className="hidden"
          />
          <CloudArrowUpIcon className="size-12 mx-auto text-blue-400" />
          <p className="text-gray-800 dark:text-base-content/80 text-md font-medium max-w-96 mx-auto">
            Choose a file or drag and drop
          </p>
          <p className="text-base-content/60 text-sm max-w-96 mx-auto">
            PDF — under {MAX_PDF_SIZE_MB}MB
          </p>
        </div>

        {/* Selected file */}
        {selectedFile && (
          <div className="mt-4 border border-base-300 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-3 bg-base-200">
              <div className="flex items-center gap-2 min-w-0">
                <DocumentIcon className="h-5 w-5 shrink-0 text-blue-400" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{selectedFile.name}</p>
                  <p className="text-xs text-base-content/60">
                    {formatFileSize(selectedFile.size)}
                    {pageCount > 0 &&
                      ` • ${pageCount} page${pageCount === 1 ? "" : "s"}`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleRemoveFile}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-300 transition-colors"
                aria-label="Remove file"
              >
                <TrashIcon className="h-5 w-5 text-red-500" />
              </button>
            </div>

            <div className="p-4 space-y-3 bg-base-100 border-t border-base-300">
              <Input
                title="Document Name *"
                value={documentName}
                onChange={(e) =>
                  setDocumentName((e.target as HTMLInputElement).value)
                }
                placeholder="e.g., Incident Report 2026-1234"
                className="w-full !max-w-full bg-base-100 !mt-0"
              />

              <TextArea
                label="Redaction Instructions (optional)"
                value={redactionInstructions}
                onChange={(e) => setRedactionInstructions(e.target.value)}
                placeholder="e.g., Only redact minors. Leave suspect and officer names visible."
                rows={2}
                maxLength={1000}
                labelSize="sm"
                labelClassName="!min-h-7 mb-1"
              />

              {/* PDF Preview */}
              {objectUrl && (
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-base-content/80 mb-2">
                    Preview
                  </p>
                  <div className="flex justify-center bg-base-200 rounded-lg p-3 max-h-[320px] overflow-auto">
                    <Document
                      file={objectUrl}
                      loading={
                        <div className="flex items-center justify-center h-48">
                          <span className="loading loading-spinner loading-md text-primary"></span>
                        </div>
                      }
                      error={
                        <div className="text-error text-sm">
                          Failed to load PDF preview
                        </div>
                      }
                    >
                      <Page
                        pageNumber={previewPage}
                        width={260}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={
                          <div
                            className="flex items-center justify-center"
                            style={{ width: 260, height: 336 }}
                          >
                            <span className="loading loading-spinner loading-md text-primary"></span>
                          </div>
                        }
                      />
                    </Document>
                  </div>
                  {pageCount > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-2">
                      <button
                        onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                        disabled={previewPage <= 1}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeftIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
                      </button>
                      <span className="text-sm text-base-content/60">
                        Page {previewPage} of {pageCount}
                      </span>
                      <button
                        onClick={() =>
                          setPreviewPage((p) => Math.min(pageCount, p + 1))
                        }
                        disabled={previewPage >= pageCount}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRightIcon className="h-4 w-4 text-gray-600 dark:text-base-content/70" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="modal-action">
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || !documentName.trim()}
            variant="primary"
            className="w-full"
          >
            Upload & Redact
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default RedactionUpload;
